import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, 
  User, 
  Lock, 
  AlertCircle, 
  Heart,
  Stethoscope,
  CheckCircle2
} from 'lucide-react';

const Login: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const success = await login(username, password);
      if (success) {
        navigate('/dashboard');
      } else {
        setError('Credenciales inválidas. Por favor, verifica tu usuario y contraseña.');
      }
    } catch (err) {
      setError('Error de conexión. Por favor, verifica tu conexión a internet.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50/70 p-4">
      <div className="max-w-md w-full animate-fade-in">
        {/* Logo y Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-24 h-24 bg-white rounded-full flex items-center justify-center mb-6 shadow-2xl p-3 border-4 border-blue-100">
            <img 
              src="/logo.png" 
              alt="Logo Hospital Divino Salvador de Sopó" 
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Sistema de Consentimientos
          </h1>
          <p className="text-blue-600 font-semibold text-lg">
            Hospital Divino Salvador de Sopó
          </p>
          <div className="mt-3 flex items-center justify-center gap-2 text-sm text-gray-500">
            <Stethoscope className="w-4 h-4" />
            <span>Plataforma Digital Segura</span>
          </div>
        </div>

        {/* Formulario de Login */}
        <div className="card shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="label flex items-center">
                <User className="w-4 h-4 mr-2 text-blue-600" />
                Usuario
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="input-field"
                placeholder="Ingresa tu usuario de Citisalud"
                required
                disabled={isLoading}
                autoComplete="username"
              />
            </div>

            <div>
              <label className="label flex items-center">
                <Lock className="w-4 h-4 mr-2 text-blue-600" />
                Contraseña
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field pr-12"
                  placeholder="Ingresa tu contraseña"
                  required
                  disabled={isLoading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? (
                    <Lock className="w-5 h-5" />
                  ) : (
                    <Lock className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-3 text-red-600 bg-red-50 p-4 rounded-lg border border-red-200">
                <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-medium">{error}</p>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary py-3 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Iniciando sesión...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Iniciar Sesión
                </span>
              )}
            </button>
          </form>

          {/* Información sobre el sistema */}
          <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
            <div className="flex items-start gap-3">
              <div className="bg-blue-100 rounded-full p-2">
                <Heart className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-800 mb-2">
                  Sistema de Gestión de Consentimientos Informados
                </p>
                <p className="text-xs text-gray-600 leading-relaxed">
                  Plataforma digital segura para la creación, gestión y almacenamiento 
                  de consentimientos informados médicos. Garantiza trazabilidad, cumplimiento 
                  normativo y protección de datos del paciente.
                </p>
              </div>
            </div>
          </div>

          {/* Características destacadas */}
          <div className="mt-6 grid grid-cols-3 gap-3 text-center">
            <div className="p-3 bg-blue-50 rounded-lg">
              <Shield className="w-6 h-6 text-blue-600 mx-auto mb-1" />
              <p className="text-xs font-medium text-gray-700">Seguro</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <CheckCircle2 className="w-6 h-6 text-green-600 mx-auto mb-1" />
              <p className="text-xs font-medium text-gray-700">Confiable</p>
            </div>
            <div className="p-3 bg-indigo-50 rounded-lg">
              <Stethoscope className="w-6 h-6 text-indigo-600 mx-auto mb-1" />
              <p className="text-xs font-medium text-gray-700">Profesional</p>
            </div>
          </div>
        </div>

        {/* Pie de página */}
        <div className="text-center mt-8">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} Hospital Divino Salvador de Sopó
          </p>
          <p className="text-gray-400 text-xs mt-1">
            Sistema seguro y confidencial • Autenticación mediante Citisalud
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
